import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { buildUrl } from '@utils/url/url.util';
import { switchMap, take } from 'rxjs/operators';
import { AssetConnectionSetting, AssetSettings, SmartChargingSettings } from '../../types/Setting';
import { RESTServerRoute } from '../../types/Server';
import { ActionResponse, CheckAssetConnectionResponse } from '../../types/DataResult';

@Injectable()
export class SettingsApiService {
  public constructor(private httpClient: HttpClient) {}

  public getSettings<T extends SmartChargingSettings>(identifier: 'smartCharging'): Observable<T> {
    return this.httpClient.get<T>(RESTServerRoute.REST_SETTINGS, {
      params: {
        Identifier: identifier,
      },
    });
  }

  public getSettingsAssetConnections(): Observable<AssetSettings> {
    return this.httpClient.get<AssetSettings>(RESTServerRoute.REST_SETTINGS, {
      params: {
        Identifier: 'asset',
      },
    });
  }

  public deleteSettingsAssetConnection(id: string): Observable<ActionResponse> {
    return this.getSettingsAssetConnections().pipe(
      take(1),
      switchMap((setting) => {
        const updated = {
          id: setting.id,
          identifier: 'asset',
          content: {
            type: 'asset',
            asset: {
              connections: setting.content.asset?.connections?.filter((c) => c.id !== id),
            },
          },
        };
        const url = buildUrl(RESTServerRoute.REST_SETTING, { id: setting.id });
        return this.httpClient.put<ActionResponse>(url, updated);
      }),
    );
  }

  public checkAssetConnection(id: string): Observable<CheckAssetConnectionResponse> {
    const url = buildUrl(RESTServerRoute.REST_ASSET_CHECK_CONNECTION, { id });
    return this.httpClient.get<CheckAssetConnectionResponse>(url);
  }

  public createSettingsAssetConnection(connection: AssetConnectionSetting): Observable<ActionResponse> {
    return this.getSettingsAssetConnections().pipe(
      take(1),
      switchMap((setting) => {
        const updated = {
          id: setting.id,
          identifier: 'asset',
          content: {
            type: 'asset',
            asset: {
              connections: [...(setting.content.asset?.connections ?? []), connection],
            },
          },
        };
        return this.httpClient.post<ActionResponse>(RESTServerRoute.REST_SETTINGS, updated, {
          params: { Identifier: 'asset' },
        });
      }),
    );
  }

  public updateSettingsAssetConnection(connection: AssetConnectionSetting): Observable<ActionResponse> {
    return this.getSettingsAssetConnections().pipe(
      take(1),
      switchMap((setting) => {
        const connections = setting.content.asset.connections?.map((c) => (c.id === connection.id ? connection : c));
        const updated = {
          id: setting.id,
          identifier: 'asset',
          content: {
            ...setting.content,
            type: 'asset',
            asset: {
              ...setting.content?.asset,
              connections,
            },
          },
        };
        const url = buildUrl(RESTServerRoute.REST_SETTING, { id: setting.id });
        return this.httpClient.put<ActionResponse>(url, updated);
      }),
    );
  }
}
